/**
 * Keeps the product page tall enough for the Buy More & Save panel.
 *
 * Above 1200 the panel is taken out of flow and pinned to the right of the buy box, so
 * that grid does not share its height out among the rows beside it. Out of flow it also
 * stops contributing any height at all, and on a product with nine tiers the panel is
 * taller than the fields it sits beside — it runs past the bottom of its own section.
 *
 * The next section down is `.shopify-section:has(.section)`, which base.css makes
 * `position: relative`. Two positioned boxes, both at z-index auto: the later one in the
 * document wins. So the product tabs painted over the foot of the panel and swallowed the
 * clicks meant for Add to basket.
 *
 * Rather than reserve a guessed amount of room, measure what the panel needs and give the
 * container exactly that. The height is only ever written when it changes — clearing it to
 * re-measure would resize the container, which would call this back, which would clear it
 * again.
 */
const CONTAINER = '.product-information__grid .group-block-content:has(> civital-volume-pricing)';

const sync = () => {
  for (const container of document.querySelectorAll(CONTAINER)) {
    const panel = container.querySelector(':scope > civital-volume-pricing');

    if (!panel) continue;

    // Below the breakpoint the panel is back in flow and sizes the container itself.
    if (getComputedStyle(panel).position !== 'absolute') {
      if (container.style.minHeight) container.style.minHeight = '';
      continue;
    }

    const styles = getComputedStyle(container);
    const edges = ['borderTopWidth', 'paddingBottom', 'borderBottomWidth'].reduce(
      (sum, side) => sum + parseFloat(styles[side] || 0),
      0
    );

    // offsetTop is measured from the container's padding edge, so the top border and the
    // panel's own offset are both already accounted for.
    let required = panel.offsetTop + panel.offsetHeight + edges;

    if (styles.boxSizing === 'content-box') {
      required -= parseFloat(styles.paddingTop || 0) + edges;
    }

    const current = parseFloat(container.style.minHeight) || 0;

    if (Math.abs(current - required) > 0.5) {
      container.style.minHeight = `${Math.ceil(required)}px`;
    }
  }
};

sync();

if ('ResizeObserver' in window) {
  const observer = new ResizeObserver(sync);

  for (const container of document.querySelectorAll(CONTAINER)) {
    observer.observe(container);

    const panel = container.querySelector(':scope > civital-volume-pricing');

    if (panel) observer.observe(panel);
  }
} else {
  window.addEventListener('resize', sync, { passive: true });
}

document.addEventListener('DOMContentLoaded', sync);
window.addEventListener('load', sync);
